import { FC, useEffect, useState } from "react";
import { ErrorMessage } from "../../components/ErrorMessage";
import { LoadingIndicator } from "../../components/LoadingIndicator";
import { MasterPullRequest, PullRequestBranch, PullRequestWorkItem } from "./PullRequests.types";
import { usePullRequests } from "./PullRequests.hook";
import "./style.css";

const requiresSoftConfig = (pullRequest: MasterPullRequest) =>
  pullRequest.workItems.some((workItem) => workItem.softConfigRequired);

const softConfigFirst = (workItems: PullRequestWorkItem[]) =>
  [...workItems].sort(
    (first, second) => Number(second.softConfigRequired) - Number(first.softConfigRequired)
  );

const formatWhen = (pullRequest: MasterPullRequest) => {
  const value = pullRequest.closedDate || pullRequest.creationDate;
  if (!value) {
    return "";
  }

  return new Date(value).toLocaleString();
};

interface Props {
  branch: PullRequestBranch;
}

export const PullRequests: FC<Props> = ({ branch }) => {
  const {
    pullRequests,
    query,
    setQuery,
    isLoading,
    error,
    softConfigFieldFound,
    total,
    visible,
  } = usePullRequests(branch);
  const [expandedIds, setExpandedIds] = useState<number[]>([]);
  const [didInit, setDidInit] = useState(false);

  useEffect(() => {
    if (isLoading || didInit) {
      return;
    }

    setExpandedIds(pullRequests.filter(requiresSoftConfig).map((pullRequest) => pullRequest.id));
    setDidInit(true);
  }, [didInit, isLoading, pullRequests]);

  if (isLoading) {
    return <LoadingIndicator isLoading />;
  }

  const allExpanded = visible > 0 && pullRequests.every((pullRequest) => expandedIds.includes(pullRequest.id));

  const toggle = (id: number) => {
    setExpandedIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    );
  };

  const toggleAll = () => {
    setExpandedIds(allExpanded ? [] : pullRequests.map((pullRequest) => pullRequest.id));
  };

  return (
    <div className="pull-requests">
      <ErrorMessage error={error} />
      {!softConfigFieldFound && (
        <p className="error-text">
          Azure DevOps has no work item field named "Soft config required".
        </p>
      )}
      <div className="pull-requests-toolbar">
        <label className="pull-requests-filter">
          <span>
            Filter ({visible} of {total})
          </span>
          <input
            value={query}
            placeholder="Title, author, work item or soft config"
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        {visible > 0 && (
          <button type="button" className="pull-requests-toggle" onClick={toggleAll}>
            {allExpanded ? "Collapse list" : "Expand list"}
          </button>
        )}
      </div>
      {visible === 0 && <p>No pull requests match this filter.</p>}
      {visible > 0 && (
        <div className="pull-request-list">
          <div className="pull-request-columns pull-request-header">
            <span />
            <span>Pull request</span>
            <span>Status</span>
            <span>Date</span>
            <span>Author</span>
            <span>Soft config</span>
          </div>
          <ul>
            {pullRequests.map((pullRequest) => {
              const softConfig = requiresSoftConfig(pullRequest);
              const expanded = expandedIds.includes(pullRequest.id);

              return (
                <li
                  className={
                    softConfig
                      ? "pull-request-item pull-request-item-soft-config"
                      : "pull-request-item"
                  }
                  key={pullRequest.id}
                >
                  <div className="pull-request-columns pull-request-summary">
                    <button
                      type="button"
                      aria-expanded={expanded}
                      aria-label={expanded ? "Collapse pull request" : "Expand pull request"}
                      onClick={() => toggle(pullRequest.id)}
                    >
                      {expanded ? "▾" : "▸"}
                    </button>
                    <a href={pullRequest.url} target="_blank" rel="noreferrer">
                      {pullRequest.id} {pullRequest.title}
                    </a>
                    <span>{pullRequest.status}</span>
                    <span>{formatWhen(pullRequest)}</span>
                    <span>{pullRequest.author}</span>
                    <span>
                      {softConfig && <span className="soft-config soft-config-yes">Yes</span>}
                    </span>
                  </div>
                  {expanded && (
                    <div className="pull-request-details">
                      {pullRequest.workItems.length === 0 && <p>No linked work items</p>}
                      {pullRequest.workItems.length > 0 && (
                        <div className="pull-request-work-item pull-request-work-item-header">
                          <span>Work item</span>
                          <span>Type</span>
                          <span>State</span>
                          <span>Soft config</span>
                        </div>
                      )}
                      {softConfigFirst(pullRequest.workItems).map((workItem) => (
                        <div className="pull-request-work-item" key={workItem.id}>
                          <a href={workItem.url} target="_blank" rel="noreferrer">
                            {workItem.id} {workItem.title}
                          </a>
                          <span>{workItem.type}</span>
                          <span>{workItem.state}</span>
                          <span
                            className={
                              workItem.softConfigRequired
                                ? "soft-config soft-config-yes"
                                : "soft-config soft-config-no"
                            }
                          >
                            {workItem.softConfigRequired ? "Yes" : "No"}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};
